import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { FormHandles } from '@unform/core'
import { Alert } from '@vtex/shoreline'
import { AxiosProgressEvent } from 'axios'
import { t } from 'i18next'

import { FeedbackContext } from '~/components/feedback'
import Loading from '~/components/Loading'
import { FileInput, FormGroup, InputHidden, Label } from '~/components/unform'
import { getImgSize } from '~/helpers'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import { campaign as service } from '~/modules/retailMedia/services/industry'

import { bypassLogoValidation } from './bypassLogoValidation'
import {
  logoImageSizes,
  MAX_SIZE_UPLOAD,
  MAX_SIZE_UPLOAD_BYTES,
  MULTI_SIZE_VALIDATION_PUBLISHERS
} from './constants'
import LogoSizeBadges from './LogoSizeBadges'

type FieldsetLogoProps = Pick<
  SponsoredBrandFieldsetProps<FormHandles>,
  'formRef' | 'initialData' | 'handleIsValid'
>

const FieldsetLogo: React.FC<FieldsetLogoProps> = ({
  formRef,
  initialData,
  handleIsValid
}) => {
  const [previewMedia, setPreviewMedia] = useState(null)
  const [fileMedia, setFileMedia] = useState<{ media_url: string }>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<boolean>(false)
  const [uploadProgress, setUploadProgress] = useState(0)

  const { tToasts } = useAPPTranslation()
  const { toast } = useContext(FeedbackContext)

  const { publisherId } = useMemo(
    () => ({ publisherId: initialData?.publisher_id }),
    [initialData]
  )

  const isMultiSizePublisher = useMemo(
    () => MULTI_SIZE_VALIDATION_PUBLISHERS.includes(publisherId),
    [publisherId]
  )

  const logoHelperText = useMemo(() => {
    if (isMultiSizePublisher) {
      return t(
        'rm:campaign.create.SponsoredBrand.FieldsetLogo.multipleSizesHelperText',
        { sizes: logoImageSizes?.map(item => item?.size || '').join(', ') }
      )
    }
    return t('rm:campaign.create.SponsoredBrand.FieldsetLogo.helperText')
  }, [isMultiSizePublisher])

  useEffect(() => {
    handleIsValid({ key: 'logo', value: !!fileMedia?.media_url })
  }, [fileMedia?.media_url, handleIsValid])

  const handleUpload = useCallback(
    async (data: File) => {
      const dataMultipart = new FormData()
      dataMultipart.append('file', data)

      setUploading(true)

      const options = {
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          const { loaded, total } = progressEvent
          const progress = Math.round((loaded * 100) / total)

          setUploadProgress(progress)

          console.debug(t('rm:progressUploadMedia', { progress }))
        }
      }

      try {
        const response = await service.uploadLogo({
          body: dataMultipart,
          axiosOptions: options
        })

        setFileMedia({
          media_url: response.data.url
        })
      } catch (error) {
        console.error(error)
        setError(true)
        tToasts({
          prefix: 'c:messages.uploadImage.error',
          toastFunction: toast.critical
        })
      } finally {
        setUploading(false)
      }
    },
    [tToasts, toast.critical]
  )

  /**
   * Before upload
   */
  const handlePrepareToUploadImage = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      setError(false)

      // Pega primeiro arquivo da lista
      const [file] = event.target.files as FileList

      const handleError = (errorMessage: string) => {
        toast.critical(errorMessage)

        setPreviewMedia(null)
        setError(true)

        formRef.current.clearField('sponsored_brand.brand_url_mediaFile')
        formRef.current.clearField('sponsored_brand.brand_url')
      }

      // Checa se tamanho é válido
      const fileIsBigger = file.size > MAX_SIZE_UPLOAD_BYTES
      if (fileIsBigger) {
        const maxSize = MAX_SIZE_UPLOAD
        const errorMessage = t('c:messages.maxSizeInMBPicture', { maxSize })

        handleError(errorMessage)

        return
      }

      // Checa se dimensões são válidas
      const dimensions: Dimension = await getImgSize(file)

      const isMultiSizePublisher =
        MULTI_SIZE_VALIDATION_PUBLISHERS.includes(publisherId)

      if (isMultiSizePublisher) {
        const isValidSize = logoImageSizes.find(
          item =>
            item?.width === dimensions?.width &&
            item?.height === dimensions?.height
        )

        if (!isValidSize) {
          const errorMessage = t('c:messages.multipleImageDimensions', {
            sizes: logoImageSizes?.map(item => item?.size || '').join(', ')
          })
          handleError(errorMessage)
          return
        }
      }

      const isSameWidth = dimensions.width === logoImageSizes[0].width
      const isSameHeight = dimensions.height === logoImageSizes[0].height
      const dimensionsNotValid = !isSameWidth && !isSameHeight

      if (!isMultiSizePublisher && dimensionsNotValid) {
        const errorMessage = t('c:messages.imageDimensions', {
          width: logoImageSizes[0].width,
          height: logoImageSizes[0].height
        })

        handleError(errorMessage)

        return
      }

      // Handle upload - sync

      handleUpload(file)

      // Update preview with selected file
      const previewURL = URL.createObjectURL(file)

      setPreviewMedia({
        type: file.type,
        url: previewURL,
        name: file.name
      })
    },
    [publisherId, handleUpload, toast, formRef]
  )

  // Check if has logo on edit
  useEffect(() => {
    if (initialData?.settings?.brand_url?.length) {
      setPreviewMedia({
        type: 'image',
        url: initialData.settings.brand_url,
        name: initialData.settings.brand_url.split('/').pop() ?? ''
      })
    }
  }, [initialData?.settings?.brand_url])

  return (
    <FormGroup className="mb-2">
      <Label
        htmlFor="brand_url_mediaFile"
        isRequired={!bypassLogoValidation(publisherId as PublisherId)}
        text={t('rm:campaign.create.SponsoredBrand.FieldsetLogo.label')}
        helperText={logoHelperText}
      />

      {error && (
        <Alert variant="critical" className="mb-2">
          {t('rm:campaign.create.FileToUpload.error.message')}
        </Alert>
      )}

      <FileInput
        name="brand_url_mediaFile"
        accept="image/*"
        onChange={handlePrepareToUploadImage}
      >
        {!!previewMedia && (
          <div className="pt-3 mt-3 border-top border-tertiary mx-auto">
            <img src={previewMedia.url} alt="" />
          </div>
        )}

        <Loading status={uploading}>
          {t('rm:campaign.create.FileToUpload.uploadingMedia')} {uploadProgress}
          %
        </Loading>

        {!previewMedia && (
          <div className="pt-3 mt-3 border-top border-tertiary">
            <h4>{t('rm:allowedMediaSizes')}</h4>

            <div className="d-flex gap 2 justify-content-center flex-wrap">
              <LogoSizeBadges isMultiSizePublisher={isMultiSizePublisher} />
            </div>
          </div>
        )}
      </FileInput>

      {!!fileMedia?.media_url && (
        <InputHidden name="brand_url" value={fileMedia?.media_url} />
      )}
    </FormGroup>
  )
}

export default FieldsetLogo
